<script setup lang="ts">
import { resolveComponent } from 'vue';
import type { CmsIconName } from '@pkg/cms-icons';
import type { GeneralLink, PageReference } from '@pkg/sanity-utils/linkQueries';
import type { Image, PortableTextBlock } from 'sanity';
import { OBJECT } from '@pkg/config/sanity/schemas';
import { TileLinkType, type TileSize } from '@pkg/sanity-utils/types/tile';
import type { ButtonProps } from '@pkg/ui/components/ui/button/Button.vue';
import { cn } from '@pkg/ui/lib/utils';
import type { ALIGNMENT } from '@pkg/sanity-utils/constants/alignment';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import { BaseTile, type TileProps } from './BaseTile';
import CmsIcon from './CmsIcon.vue';

export interface TilesGridTileProps {
  variant: TileProps['variant'];
  type:
    | OBJECT.MODULAR_INNER_BLOCK_SOLUTION_TILES
    | OBJECT.MODULAR_INNER_BLOCK_TILES;
  size?: TileSize;
  tile: {
    _key: string;
    alignment: ALIGNMENT;
    icon?: CmsIconName;
    preTitle?: string;
    title: string;
    tag?: string;
    date?: string;
    image?: Image;
    subImage?: Image;
    content: Array<PortableTextBlock>;
    link?: {
      type?: TileLinkType;
      link: GeneralLink | PageReference;
      title: string;
      label?: string;
    };
  };
}

defineProps<TilesGridTileProps>();

const getButtonVariant = (type?: TileLinkType): ButtonProps['variant'] => {
  if (type === TileLinkType.BUTTON) {
    return 'default';
  }
  if (type === TileLinkType.TEXT_BUTTON) {
    return 'text';
  }

  return 'link';
};

/** Host app registers BaseImage from @pkg/ui. */
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <BaseTile
    v-if="type === OBJECT.MODULAR_INNER_BLOCK_TILES"
    :as-link="!!tile.link"
    :title="tile.title"
    :pre-title="tile.preTitle"
    :variant="variant"
    class="c-grid-tile"
    :description="tile.content"
    :tag="tile.tag"
    :date="tile.date"
    :size="size"
  >
    <template #image>
      <component :is="BaseImage" :image="tile.image" :height="200" :width="200" />
    </template>
    <template v-if="tile.icon" #icon>
      <CmsIcon :name="tile.icon" class="text-56" />
    </template>
    <UiButton
      v-if="tile.link"
      :variant="getButtonVariant(tile.link.type)"
      :no-text-transform="tile.link.type === TileLinkType.TEXT"
      :class="cn({ 'font-medium': tile.link.type === TileLinkType.TEXT })"
      as="span"
    >
      {{ tile.link.title }}
    </UiButton>
  </BaseTile>
</template>

<style scoped>
.c-grid-tile {
  display: flex;
  flex: 1;

  &:deep(.c-tile--media .c-tile__image) {
    height: auto;
  }

  &:deep(.c-tile__image img) {
    padding: 8px;
    margin-top: 24px;
    max-height: 160px;
    max-width: 160px;
    object-position: center;
    object-fit: cover;
  }

  &:deep(.c-tile__content){
    padding-top: 16px;
  }
}

</style>
